/**
 * How a GroundTruth review names an instruction.
 *
 * The gt-api worker keys every instruction on an internal id — U2 for the
 * second line of the typed Additional Instructions, A1 for the first line of
 * the "Additional Notes from AM" — and the report cross-references on it. On
 * screen that notation means nothing, so reviewers read "Instruction 2" and
 * "AM note 1" instead. gt_io_instructions.instruction_label /
 * humanize_instruction_refs are the server-side twins: new reports are written
 * with these labels, and the rewrite below keeps reports written before them
 * (already sitting in review) reading the same. Only those are rewritten — a
 * newer report's text is shown as written, since it may quote the plan's own
 * names.
 */

// The worker version that still wrote the ids into its messages.
const ID_LABELLED_BEFORE = 'gt-io-instructions/1.0.0';

const PREFIX = { U: 'Instruction', A: 'AM note' };
const INSTRUCTION_ID = /^([UA])(\d+)$/;
// Exactly the phrasings the worker used to write ids in: "Instruction U1 (...)",
// "[Instruction U2 · ...]", "instruction U3: ...", "instruction U1 is marked
// ...", "instructions U1, A2 could not be interpreted", "U1 says 'CPC'",
// "operation ('U9', 'set_field')". Each branch requires what followed the id
// there, so user text quoted inside a message ("Special Instruction A1
// Shoppers", "A35+ Demo") is left alone. Same pattern as the worker's
// _LEGACY_INSTRUCTION_REF; no lookbehind, which older Safari cannot parse.
const LEGACY_REF = /\b[Ii]nstructions? ([UA]\d+(?:, [UA]\d+)*)(?= \(| ·|:| could\b| is\b)|\b([UA]\d+)(?= says (?:['"\d[{(-]|None\b|True\b|False\b))|operation \('([UA]\d+)'(?=, )/g;

/** "U2" → "Instruction 2", "A1" → "AM note 1"; null when there is no id. */
export function instructionLabel(instructionId) {
  if (instructionId === null || instructionId === undefined || instructionId === '') return null;
  if (String(instructionId).startsWith('R:')) return `Standard rule ${String(instructionId).slice(2)}`;
  const match = INSTRUCTION_ID.exec(String(instructionId));
  if (!match) return `Instruction ${instructionId}`;
  return `${PREFIX[match[1]]} ${match[2]}`;
}

/** True for a report whose messages name instructions by internal id. */
export function writesInternalIds(report) {
  const version = report?.processor_version;
  return !version || version === ID_LABELLED_BEFORE;
}

/** Rewrite internal instruction ids inside a message. Idempotent. */
export function humanizeInstructionRefs(text) {
  if (typeof text !== 'string' || !text) return text;
  return text.replace(LEGACY_REF, (_all, list, says, discarded) => {
    if (list) return list.split(', ').map(instructionLabel).join(', ');
    if (says) return instructionLabel(says);
    return `operation (${instructionLabel(discarded)}`;
  });
}

/**
 * A CTS finding as the review shows it. Only instruction findings written with
 * the ids are rewritten: "[Instruction U2 · …]", and "[Instructions] …" (which
 * never names an instruction in its prefix). A newer "[Instruction 2 · …]" /
 * "[AM note 1 · …]" finding, or a validator message, is shown as written.
 */
export function displayFinding(message) {
  if (typeof message !== 'string' || !/^\[(?:Instruction [UA]\d+ · |Instructions\] )/.test(message)) return message;
  return humanizeInstructionRefs(message);
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * A message shown beside its own instruction's badge, without repeating the
 * label the badge already carries: "Instruction 1 (Additional Instructions):
 * ages 18-55 were applied as 18-54" → "Ages 18-55 were applied as 18-54" —
 * also when the label is still the id ("Instruction U1 (…): …"). A message that
 * names a different instruction — or none — is kept whole.
 */
export function withoutOwnLabel(message, instructionId) {
  const label = instructionLabel(instructionId);
  if (typeof message !== 'string' || !label) return message;
  const names = `(?:${escapeRegExp(label)}|Instruction ${escapeRegExp(String(instructionId))})`;
  const own = new RegExp(`^${names}(?: \\([^)]*\\))?(?::\\s*|\\s+)`);
  if (!own.test(message)) return message;
  const rest = message.replace(own, '');
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : message;
}
